import * as React from 'react';
import { FixMeLater } from '../../utils';
import { PAPER_STATIC_STYLES, usePaperCssVariables, usePaperDynamicStyles } from './Paper.styles';
import { Styler, StylerBaseProps } from '../Styler';
import {
    HoneycombRadius,
    HoneycombShadowSize,
    PaperCornerStyle,
    ResponsiveStyleValue,
    useComponentDefaultProps,
} from '../../theming';

export interface PaperProps extends StylerBaseProps {
    /**
     * The width of the Paper's border. If `0`, no border will be visible.
     * @default 1
     */
    border?: number;

    /**
     * The Paper's corner radius. Only relevant when cornerStyle="rounded".
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default m
     */
    radius?: ResponsiveStyleValue<HoneycombRadius | string>;

    /**
     * A shadow from the theme.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default none
     */
    shadow?: ResponsiveStyleValue<HoneycombShadowSize>;

    /**
     * If `true`, the Paper will have the default responsive left and right padding.
     * @default false
     */
    defaultGutters?: boolean;

    /**
     * If `rounded`, a border radius will be applied as specified by the radius property.
     * If `square`, the radius property will be ignored and corners will be square.
     * This property can be used to disable the radius on all Paper components using default props.
     * @default rounded
     */
    cornerStyle?: PaperCornerStyle;
}

const defaultProps: Partial<PaperProps> = {
    border: 1,
    radius: 'm',
    defaultGutters: false,
    cornerStyle: 'rounded',
};

/**
 * Paper is a container that displays content on a themeable surface.
 * The border, radius, and shadow properties should be customised according to the requirements of the theme.
 * Paper serves as the base component for other surface components such as Card and Dialog.
 */
export const Paper: React.ForwardRefExoticComponent<PaperProps & React.RefAttributes<HTMLElement>> = React.forwardRef(
    function Paper(props: PaperProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            border = 0,
            shadow = 'none',
            radius: radiusProp = 'm',
            defaultGutters = false,
            cornerStyle = 'rounded',
            ...other
        } = useComponentDefaultProps('Paper', defaultProps, props);

        const withRadius = cornerStyle === 'rounded';
        const radius = withRadius ? radiusProp : 'none';

        const cssVariables = usePaperCssVariables(border);
        const dynamicStyles = usePaperDynamicStyles(shadow, radius);

        return (
            <Styler
                ref={ref}
                style={cssVariables}
                baseStyles={[PAPER_STATIC_STYLES, dynamicStyles]}
                data-default-gutters={defaultGutters || undefined}
                {...other}
            />
        );
    }
);
